import { useQuery } from "@tanstack/react-query";
import { checkPermission } from "~/api/permissions/api";
import type { Action, ResourceType } from "~/api/permissions/api";

export interface UsePermissionOptions {
  /** Skip the check and return false immediately (useful for conditional enabling) */
  skip?: boolean;
}

export interface UsePermissionResult {
  allowed: boolean;
  loading: boolean;
}

/**
 * Hook to check a Cerbos permission for the current user.
 *
 * Replaces hardcoded role checks like `role === "GXM_ADMIN"` with a proper
 * backend permission check that evaluates the Cerbos policy.
 *
 * @example
 * // Replace: {role === "GXM_ADMIN" && <Button />}
 * // With:
 * const { allowed } = usePermission("employee", "create", "new", { entity_id: entityId });
 * {allowed && <Button />}
 *
 * @example
 * // For upload buttons scoped to an entity:
 * const { allowed } = usePermission("employee", "modify", employeeId, { entity_id: entityId });
 * {allowed && <CustomeButton label="Upload File" />}
 */
export function usePermission(
  resource: ResourceType,
  action: Action,
  resourceId?: string,
  resourceAttr?: Record<string, unknown>,
  options?: UsePermissionOptions,
): UsePermissionResult {
  const { data, isLoading } = useQuery({
    queryKey: ["permission", resource, action, resourceId, resourceAttr],
    queryFn: () =>
      checkPermission({
        resource,
        action,
        resourceId: resourceId ?? "new",
        resourceAttr: resourceAttr ?? {},
      }),
    enabled: !options?.skip,
    staleTime: 30_000,
  });

  return {
    allowed: data ?? false,
    loading: isLoading,
  };
}
